// src/components/pages/HomePage.js
import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    NAVBAR_HEIGHT,
    BOTTOM_ACTION_BUTTONS_POSITION,
} from '../../constants/constants';
import { useLoadScript } from '@react-google-maps/api';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { distanceBetween } from 'geofire-common';

import IconMessageDefault from '../../assets/icons/message-default.svg';
import IconBuryTimeCapsule from '../../assets/icons/icon-bury-capsule.png';
import ActionButton from '../atoms/ActionButton';

import useTimeCapsuleMarkers from '../../hooks/useTimeCapsuleMarkers';
import MapControls from '../MapControls';
import TimeCapsuleMap from '../TimeCapsuleMap';
import SelectedCapsuleOverlay from '../SelectedCapsuleOverlay';

const MAP_LIBRARIES = ['places'];

export default function HomePage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const geocoderRef = useRef(null);
    const watchIdRef = useRef(null);
    const initialUserLocationRef = useRef(null);

    // CSS-variable trick for real viewport height
    useEffect(() => {
        const setVh = () =>
            document.documentElement.style.setProperty(
                '--vh',
                `${window.innerHeight * 0.01}px`
            );
        setVh();
        window.addEventListener('resize', setVh);
        return () => window.removeEventListener('resize', setVh);
    }, []);

    const [searchText, setSearchText] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const [mapCenter, setMapCenter] = useState({
        lat: 40.7128,
        lng: -74.0060,
    });
    const [selected, setSelected] = useState(null);
    const [geoError, setGeoError] = useState(null);
    const [directions, setDirections] = useState(null);
    const [travelTime, setTravelTime] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [followMode, setFollowMode] = useState(false);

    // <-- track drag overlay
    const [isDragging, setIsDragging] = useState(false);

    const { isLoaded, loadError } = useLoadScript({
        id: 'google-maps-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    // 1) Initial GPS fix
    useEffect(() => {
        const saved = localStorage.getItem('userLocation');
        if (saved) {
            try {
                const pos = JSON.parse(saved);
                setUserLocation(pos);
                setMapCenter(pos);
                return;
            } catch { }
        }
        if (!navigator.geolocation) {
            setGeoError('Geolocation not supported');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                const pos = {
                    lat: coords.latitude,
                    lng: coords.longitude,
                };
                setUserLocation(pos);
                setMapCenter(pos);
                localStorage.setItem('userLocation', JSON.stringify(pos));
                initialUserLocationRef.current ||= pos;
            },
            err => setGeoError(err.message),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, []);

    // 2) Capture map & create Geocoder
    const onMapLoad = useCallback(map => {
        mapRef.current = map;
        geocoderRef.current = new window.google.maps.Geocoder();
    }, []);

    // 3) Recenter button: clear the drag-pin
    const handleRecenter = useCallback(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                const pos = { lat: coords.latitude, lng: coords.longitude };
                setUserLocation(pos);
                setMapCenter(pos);
                mapRef.current?.panTo(pos);
                if (!directions) mapRef.current?.setZoom(17);
                setGeoError(null);
                setFollowMode(true);
                setIsDragging(false);          // ← clear overlay here

                geocoderRef.current?.geocode(
                    { location: pos },
                    (results, status) => {
                        if (status === 'OK' && results[0]) {
                            setSearchText(results[0].formatted_address);
                        }
                    }
                );
            },
            err => setGeoError(err.message),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, [directions]);

    // 6️⃣ confirm → navigate on bury
    const handleConfirmLocation = useCallback(() => {
        if (!mapCenter || !searchText) {
            console.warn('No location data to confirm');
            return;
        }
        const { lat, lng } = mapCenter;
        const address = encodeURIComponent(searchText);
        navigate(`/timecapsule?lat=${lat}&lng=${lng}&address=${address}`);
    }, [mapCenter, searchText, navigate]);

    // 4) Load nearby time-capsule markers
    const { markers } = useTimeCapsuleMarkers(mapCenter, {
        radius: 1000,
    });

    // 5) Auto-select nearest marker when not navigating
    useEffect(() => {
        if (isNavigating || !markers.length) return;
        const nearest = markers.reduce((a, b) => {
            const da = distanceBetween(
                [mapCenter.lat, mapCenter.lng],
                [a.position.lat, a.position.lng]
            );
            const db = distanceBetween(
                [mapCenter.lat, mapCenter.lng],
                [b.position.lat, b.position.lng]
            );
            return db < da ? b : a;
        });
        setSelected(nearest);
        setDirections(null);
        setTravelTime(null);
    }, [markers, mapCenter, isNavigating]);

    // 6) Calculate walking route
    const calculateRoute = useCallback(
        destination => {
            if (!userLocation || !destination) return;
            const service = new window.google.maps.DirectionsService();
            service.route(
                {
                    origin: userLocation,
                    destination,
                    travelMode: window.google.maps.TravelMode.WALKING,
                },
                (result, status) => {
                    if (status === 'OK' && result) {
                        setDirections(result);
                        const leg = result.routes[0].legs[0];
                        setTravelTime(leg.duration.text);
                        setIsNavigating(true);
                        setFollowMode(true);
                    } else {
                        console.error('Directions failed:', status);
                    }
                }
            );
        },
        [userLocation]
    );

    // 7) Live-follow GPS update
    useEffect(() => {
        if (isNavigating && navigator.geolocation && !watchIdRef.current) {
            watchIdRef.current = navigator.geolocation.watchPosition(
                ({ coords }) => {
                    const pos = {
                        lat: coords.latitude,
                        lng: coords.longitude,
                    };
                    setUserLocation(pos);
                    mapRef.current?.panTo(pos);
                    if (coords.heading != null) {
                        mapRef.current?.setHeading(coords.heading);
                    }
                },
                err => console.error('watchPosition error:', err),
                { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
            );
        } else if (!isNavigating && watchIdRef.current) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
        return () => {
            if (watchIdRef.current) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, [isNavigating]);

    // 8) Drag start → show overlay
    const handleMapDrag = useCallback(() => {
        setIsDragging(true);
    }, []);

    // 9) Idle no longer hides it — stays until recenter clears it

    const handleMapIdle = useCallback(() => {
        const c = mapRef.current?.getCenter();
        if (!c) return;
        const newC = { lat: c.lat(), lng: c.lng() };
        if (
            newC.lat !== mapCenter.lat ||
            newC.lng !== mapCenter.lng
        ) {
            setMapCenter(newC);
            geocoderRef.current?.geocode(
                { location: newC },
                (results, status) => {
                    if (status === 'OK' && results[0]) {
                        setSearchText(results[0].formatted_address);
                    }
                }
            );
        }
    }, [mapCenter]);

    const handleMarkerClick = useCallback(m => {
        mapRef.current?.panTo(m.position);
        setSelected(m);
        setDirections(null);
        setTravelTime(null);
    }, []);

    const clearSelection = useCallback(() => {
        setSelected(null);
        setDirections(null);
        setTravelTime(null);
        setIsNavigating(false);
        setFollowMode(false);
        mapRef.current?.setHeading(0);
        const init = initialUserLocationRef.current;
        if (init) {
            mapRef.current?.panTo(init);
            setMapCenter(init);
        }
    }, []);

    // 10) Map options & container style
    const mapOptions = useMemo(
        () => ({
            disableDefaultUI: true,
            zoomControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
        }),
        []
    );
    const containerStyle = useMemo(
        () => ({
            width: '100%',
            height: `calc(var(--vh,1vh)*100 - ${NAVBAR_HEIGHT}px)`,
        }),
        []
    );

    if (loadError) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography color="error">
                    Error loading Google Maps
                </Typography>
            </Box>
        );
    }
    if (!isLoaded) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    height: '100vh',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                ...containerStyle,
            }}
        >
            {geoError && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        width: '100%',
                        textAlign: 'center',
                        zIndex: 9,
                    }}
                >
                    <Typography color="error">
                        Unable to fetch location: {geoError}
                    </Typography>
                </Box>
            )}

            <MapControls
                searchText={searchText}
                onSearchChange={setSearchText}
                onSelect={place => {
                    const loc = place.geometry.location;
                    mapRef.current?.panTo({
                        lat: loc.lat(),
                        lng: loc.lng(),
                    });
                    mapRef.current?.setZoom(13);
                    setSearchText(place.formatted_address);
                    setMapCenter({
                        lat: loc.lat(),
                        lng: loc.lng(),
                    });
                }}
                onRecenter={handleRecenter}
                followMode={followMode}
                theme={theme}
            />

            <TimeCapsuleMap
                center={mapCenter}
                userLocation={userLocation}
                markers={markers}
                directions={directions}
                onLoad={onMapLoad}
                onDrag={handleMapDrag}
                onIdle={handleMapIdle}
                onMarkerClick={handleMarkerClick}
                mapOptions={mapOptions}
                containerStyle={containerStyle}
            />

            {/* overlay pin */}
            {isDragging && (
                <Box
                    component="img"
                    src={IconBuryTimeCapsule}
                    alt="drop pin"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -100%)',
                        pointerEvents: 'none',
                        zIndex: 11,
                        width: 32,
                        height: 32,
                    }}
                />
            )}

            {selected && (
                <SelectedCapsuleOverlay
                    capsule={selected}
                    travelTime={travelTime}
                    onWalkClick={() =>
                        calculateRoute(selected.position)
                    }
                    onViewDetails={() =>
                        navigate(`/capsule/${selected.id}`)
                    }
                    onClose={clearSelection}
                />
            )}

            {/* ▶︎ Bottom action buttons */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: `${BOTTOM_ACTION_BUTTONS_POSITION}px`,
                    left: 16,
                    right: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 2,
                }}
            >
                <ActionButton
                    icon={IconMessageDefault}
                    text="Confirm Drop Location"
                    onClick={handleConfirmLocation}
                    sx={theme => ({
                        backgroundColor: theme.colors.white,
                        border: `1px solid ${theme.palette.primary.main}`,
                        color: theme.palette.primary.main,
                        pl: '10px',
                        pt: '10px',
                        pb: '10px',
                        boxShadow: 'none',
                        textTransform: 'none',
                        flex: 'none',
                    })}
                />
            </Box>
        </Box>
    );
}